import { useEffect, useRef, useState } from "react";

import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  TouchSensor,
  useDraggable,
  MouseSensor,
  pointerWithin,
} from "@dnd-kit/core";
import classNames from "classnames";
import { POKE_DATA } from "game/data/pokemon/data";
import { PokemonType } from "types/battle-engine/battle";
import {
  DeleteBoxInput,
} from "types/web-app";

import { Alert } from "@/components/alert/alert";
import { BoxEditModal } from "@/components/box/box-edit-modal";
import { CreateBoxDialog } from "@/components/box/create-box-dialog";
import { DeleteBoxConfirmDialog } from "@/components/box/delete-box-confirm-dialog";
import { MoveToBoxDialog } from "@/components/box/move-to-box-dialog";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import { ReleaseConfirmDialog } from "@/components/ui/dialog/release-confirm-dialog";
import { ClearGlassFrame } from "@/components/ui/frames/clear-glass-frame";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { StrokedText } from "@/components/ui/text/stroked-text";
import {
  FilterIcon,
  SearchIcon,
  EyeIcon,
  EyeOffIcon,
  StarIcon,
  TrashIcon,
  BackIcon,
  PencilIcon,
} from "@/icons";
import { useAuth } from "@/providers/auth-provider";
import { Intent, useSPAContext } from "@/providers/spa-provider";
import { updateItemDeck } from "@/services/item-deck/update-item-deck";
import { RouteNames } from "@/types/route-names";

import { BoxTabs } from "./box-tabs";

import { releasePokemon } from "@/services/pokemon/release-pokemon";
import { markSeen } from "@/services/pokemon/mark-seen";

import { TCGPokeItem } from "./tcg-poke-item";
import { CardPokeItem } from "./card-poke-item";

import type {
  DragEndEvent,
  DragStartEvent} from "@dnd-kit/core";
import type { PokeNames } from "types/pokemon/poke-names";
import type {
  InventoryPokemonsOutput,
  PokemonBoxData,
  UpdateBoxInput} from "types/web-app";

import { useUser } from "@/providers/user-provider";
import { movePokemons } from "@/services/box/move-pokemons";
import { updateBox } from "@/services/box/update-box";
import { deleteBox } from "@/services/box/delete-box";

// Type color mapping
const typeColors: Record<string, string> = {
  normal: "#a8a878",
  fire: "#f08030",
  water: "#6890f0",
  electric: "#f8d030",
  grass: "#78c850",
  ice: "#98d8d8",
  fighting: "#c03028",
  poison: "#a040a0",
  ground: "#e0c068",
  flying: "#a890f0",
  psychic: "#f85888",
  bug: "#a8b820",
  rock: "#b8a038",
  ghost: "#705898",
  dragon: "#7038f8",
  dark: "#705848",
  steel: "#b8b8d0",
  fairy: "#ee99ac",
};

const VIEW_MODE_GRID_COLS = {
  mini: 4,
  mid: 2,
  card: 3,
} as const;

interface InventoryListProps {
  pokemons: InventoryPokemonsOutput["player_pokemons"] | undefined;
  isLoading: boolean;
  error: string | null;
  onRefresh: () => Promise<void>;
  boxes: PokemonBoxData[];
}

const DraggablePokemon = ({
  id,
  children,
  className,
  disabled = false,
}: {
  id: number;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `pokemon-${id}`,
    data: { id },
    disabled,
  });

  return (
    <div
      id={`pokemon-${id}`}
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={classNames(className, { "opacity-50": isDragging })}
      style={{ touchAction: "pan-y" }}
    >
      {children}
    </div>
  );
};

// Storage key for inventory state persistence
const INVENTORY_STATE_KEY = "pokegram_inventory_state";

interface InventoryState {
  selectedBoxId: string;
  viewMode: keyof typeof VIEW_MODE_GRID_COLS;
  searchQuery: string;
  selectedTypes: PokemonType[];
  selectedTalentTypes: PokemonType[];
  talentFilterCount: number;
  isFilterMinimized: boolean;
  scrollPosition: number;
}

export const InventoryList = ({
  pokemons,
  isLoading,
  error,
  onRefresh,
  boxes,
}: InventoryListProps) => {
  const {
    intent,
    selectedPokemons,
    setSelectedPokemons,
    setIntent,
    setCurrentRoute,
    setPreviousRoute,
    currentRoute,
  } = useSPAContext();
  const { mainDeckPokemons, mainDeckId, reloadProfile } = useUser();
  const { getAccessToken } = useAuth();

  // Load saved state from localStorage
  const loadSavedState = (): Partial<InventoryState> => {
    // Don't restore state if we're in an intent context
    if (intent) {
      localStorage.removeItem(INVENTORY_STATE_KEY);
      return {};
    }

    try {
      const saved = localStorage.getItem(INVENTORY_STATE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      console.warn("Failed to load inventory state:", e);
      return {};
    }
  };

  // Save current state to localStorage
  const saveCurrentState = (additionalState: Partial<InventoryState> = {}) => {
    const state: InventoryState = {
      selectedBoxId,
      viewMode,
      searchQuery,
      selectedTypes,
      selectedTalentTypes,
      talentFilterCount,
      isFilterMinimized,
      scrollPosition: 0, // Will be updated by scroll handler
      ...additionalState,
    };

    try {
      localStorage.setItem(INVENTORY_STATE_KEY, JSON.stringify(state));
    } catch (e) {
      console.warn("Failed to save inventory state:", e);
    }
  };

  const savedState = loadSavedState();

  const [selectedBoxId, setSelectedBoxId] = useState<string>(
    savedState.selectedBoxId || "all",
  );
  const [viewMode, setViewMode] = useState<keyof typeof VIEW_MODE_GRID_COLS>(
    savedState.viewMode || "mini",
  );

  const [searchQuery, setSearchQuery] = useState<string>(
    savedState.searchQuery || "",
  );
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] =
    useState<boolean>(false);
  const [selectedTypes, setSelectedTypes] = useState<PokemonType[]>(
    savedState.selectedTypes || [],
  );
  const [selectedTalentTypes, setSelectedTalentTypes] = useState<PokemonType[]>(
    savedState.selectedTalentTypes || [],
  );
  const [talentFilterCount, setTalentFilterCount] = useState<number>(
    savedState.talentFilterCount || 1,
  );
  const [isFilterMinimized, setIsFilterMinimized] = useState<boolean>(
    savedState.isFilterMinimized || false,
  );
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  const [showReleaseConfirm, setShowReleaseConfirm] = useState<boolean>(false);
  const [showMoveDialog, setShowMoveDialog] = useState<boolean>(false);
  const [showCreateBoxDialog, setShowCreateBoxDialog] =
    useState<boolean>(false);
  const [showEditBoxModal, setShowEditBoxModal] = useState<boolean>(false);
  const [showDeleteBoxConfirm, setShowDeleteBoxConfirm] =
    useState<boolean>(false);
  const [isReleasing, setIsReleasing] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isMultiSelecting, setIsMultiSelecting] = useState<boolean>(!!intent);

  // DND State
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  // Optimistic UI State
  const [optimisticPokemons, setOptimisticPokemons] = useState<
    InventoryPokemonsOutput["player_pokemons"]
  >(pokemons || []);

  useEffect(() => {
    if (pokemons) {
      setOptimisticPokemons(pokemons);
    }
  }, [pokemons]);

  // Save state to localStorage when relevant state changes
  useEffect(() => {
    saveCurrentState();
  }, [
    selectedBoxId,
    viewMode,
    searchQuery,
    selectedTypes,
    selectedTalentTypes,
    talentFilterCount,
    isFilterMinimized,
  ]);

  // Clear saved state when navigating away from inventory
  useEffect(() => {
    if (currentRoute && currentRoute !== RouteNames.INVENTORY_POKE) {
      localStorage.removeItem(INVENTORY_STATE_KEY);
    }
  }, [currentRoute]);

  const handleOptimisticMove = async (
    targetBoxId: string | null,
    pokemonIds: number[],
  ) => {
    if (!getAccessToken()) return;

    // Optimistic Update
    const previousState = [...optimisticPokemons];
    setOptimisticPokemons((prev) =>
      prev.map((p) => {
        if (pokemonIds.includes(p.id)) {
          return { ...p, box_id: targetBoxId };
        }
        return p;
      }),
    );

    // Call API
    try {
      const response = await movePokemons({
        pokemon_ids: pokemonIds,
        target_box_id:
          targetBoxId === "all" || targetBoxId === "default"
            ? null
            : targetBoxId,
      });

      if (response.ok) {
        void onRefresh();
        // Clear selection if we moved selected items
        const hasMovedSelected = selectedPokemons.some((p) =>
          pokemonIds.includes(p.id),
        );
        if (hasMovedSelected) {
          setSelectedPokemons([]);
          setIsMultiSelecting(false);
        }
      } else {
        // Revert
        setOptimisticPokemons(previousState);
        Alert(response.message || "Failed to move Pokémon");
      }
    } catch (e) {
      console.error(e);
      setOptimisticPokemons(previousState);
      Alert("Failed to move Pokémon");
    }
  };

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 350,
        tolerance: 5,
      },
    }),
  );

  const handleUpdateBox = async (input: UpdateBoxInput) => {
    if (!getAccessToken()) return;

    try {
      const response = await updateBox(input);
      if (response.ok) {
        void onRefresh();
      } else {
        Alert(response.message || "Failed to update squad");
      }
    } catch (e) {
      console.error(e);
      Alert("Failed to update squad");
    }
  };

  const handleDeleteBox = async () => {
    if (!selectedBox) return;

    if (!getAccessToken()) return;

    setIsDeleting(true);
    try {
      const response = await deleteBox({ box_id: selectedBox.id });
      if (response.ok) {
        // If we're currently viewing the deleted box, switch to "all"
        if (selectedBoxId === selectedBox.id) {
          setSelectedBoxId("all");
        }
        void onRefresh();
        setShowDeleteBoxConfirm(false);
        Alert("Squad deleted successfully");
      } else {
        Alert(response.message || "Failed to delete squad");
      }
    } catch (e) {
      console.error(e);
      Alert("Failed to delete squad");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);

    const pokemonId = event.active.data.current?.id;
    if (pokemonId) {
      const pokemon = optimisticPokemons?.find((p) => p.id === pokemonId);
      if (pokemon) {
        if (!isSelected(pokemon)) {
          setSelectedPokemons([pokemon]);
        }
        setIsMultiSelecting(true);
      }
    }
  };

  const handleDragMove = () => {};

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragId(null);

    if (!over) return;

    // Check if dropped on a box
    const overId = over.id as string;
    if (typeof overId === "string" && overId.startsWith("box-")) {
      const targetBoxId = over.data.current?.boxId;
      const pokemonId = active.data.current?.id;

      if (pokemonId && targetBoxId !== undefined) {
        // Determine if we are moving multiple or single
        // If the dragged pokemon is part of the selection, move all selected
        // Otherwise, move only the dragged one
        const isDraggedSelected = selectedPokemons.some(
          (p) => p.id === pokemonId,
        );

        const pokemonIdsToMove = isDraggedSelected
          ? selectedPokemons.map((p) => p.id)
          : [pokemonId];

        // Convert targetBoxId to the format expected by handleOptimisticMove
        // Drag data might return "default" or "all" string, or null?
        // Checking usage: box-tabs.tsx sets boxId in data.
        // Usually it's the box.id. If it's "all", we probably shouldn't be able to drop on "all" unless logic allows.
        // Assuming targetBoxId is the valid ID from the drop zone.
        // But handleOptimisticMove handles conversion for API if it's "all" or "default".
        // Let's pass it as is, but ensure type compatibility.
        // box-tabs usually uses box IDs. "default" box usually has id="default" or null in DB but represented as something in UI.
        // The API expects null for default box.
        // handleOptimisticMove expects string | null.

        const targetId =
          targetBoxId === "default" || targetBoxId === "all"
            ? null
            : targetBoxId;

        void handleOptimisticMove(targetId, pokemonIdsToMove);
      }
    }
  };

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target as Node)
      ) {
        setIsFilterDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const nextViewMode = () => {
    setViewMode((prev) => {
      const next =
        (Object.keys(VIEW_MODE_GRID_COLS).indexOf(prev) + 1) %
        Object.keys(VIEW_MODE_GRID_COLS).length;
      return Object.keys(VIEW_MODE_GRID_COLS)[
        next
      ] as keyof typeof VIEW_MODE_GRID_COLS;
    });
  };

  const isSelected = (
    pokemon: InventoryPokemonsOutput["player_pokemons"][0],
  ): boolean => {
    return selectedPokemons.some(
      (selected: InventoryPokemonsOutput["player_pokemons"][0]) =>
        selected.id === pokemon.id,
    );
  };

  const handleMarkAsSeen = async (pokemonId: number) => {
    try {
      if (getAccessToken()) {
        await markSeen({ pokemon_id: pokemonId });
        await onRefresh();
      }
    } catch (e) {
      console.error("Failed to mark as seen", e);
    }
  };

  const handleAddPokemonToMainDeck = async (
    pokemon: InventoryPokemonsOutput["player_pokemons"][0],
  ) => {
    if (!mainDeckId) {
      Alert("No main deck found. Create a deck first and set it as main.");
      return;
    }

    try {
      if (!getAccessToken()) return;

      const existingPokemonIds = mainDeckPokemons.map((p) => p.id);
      const pokeSlots = [...existingPokemonIds, pokemon.id].slice(0, 8);

      const response = await updateItemDeck({
        deck_id: mainDeckId,
        poke_slots: pokeSlots,
      });

      if (response.ok) {
        await reloadProfile();
        setIntent(undefined);
        setCurrentRoute(undefined);
      } else {
        Alert(response.message || "Failed to add Pokémon to deck");
      }
    } catch (err) {
      console.error(err);
      Alert("An error occurred while adding Pokémon to deck");
    }
  };

  const handleSelectPokemon = (
    pokemon: InventoryPokemonsOutput["player_pokemons"][0],
  ): void => {
    if (isSelected(pokemon)) {
      if (selectedPokemons.length === 1) {
        setIsMultiSelecting(false);
      }
      setSelectedPokemons(
        selectedPokemons.filter(
          (selected: InventoryPokemonsOutput["player_pokemons"][0]) =>
            selected.id !== pokemon.id,
        ),
      );
      return;
    }

    if (selectedPokemons.length >= 8) {
      setSelectedPokemons(
        (prev: InventoryPokemonsOutput["player_pokemons"]) => [
          ...prev.slice(1),
          pokemon,
        ],
      );

      return;
    }

    setSelectedPokemons([...selectedPokemons, pokemon]);
  };

  const handleClickPokemon = (
    pokemon: InventoryPokemonsOutput["player_pokemons"][0],
  ) => {
    if (intent === Intent.SELECTING_POKEMONS_TO_TRAIN) {
      handleSelectPokemon(pokemon);
      return;
    }

    if (intent === Intent.SELECTING_POKEMON_FOR_MAIN_DECK) {
      if (mainDeckPokemons.some((p) => p.id === pokemon.id)) {
        return;
      }
      // Clear saved state when navigating for main deck selection
      localStorage.removeItem(INVENTORY_STATE_KEY);
      void handleAddPokemonToMainDeck(pokemon);
      return;
    }

    if (isMultiSelecting) {
      handleSelectPokemon(pokemon);
    } else {
      if (pokemon.is_new) {
        void handleMarkAsSeen(pokemon.id);
      }

      setSelectedPokemons([pokemon]);
      setPreviousRoute(RouteNames.INVENTORY_POKE);
      setCurrentRoute(RouteNames.POKEMON_INFO);
    }
  };

  const handleLongPressPokemon = (
    pokemon: InventoryPokemonsOutput["player_pokemons"][0],
  ) => {
    if (isMultiSelecting) {
      handleSelectPokemon(pokemon);
      return;
    }

    setIsMultiSelecting(true);
    setSelectedPokemons([pokemon]);
  };

  const handleBulkRelease = async () => {
    if (selectedPokemons.length === 0) return;

    setIsReleasing(true);
    try {
      if (!getAccessToken()) return;

      for (const p of selectedPokemons) {
        await releasePokemon({ pokemon_id: p.id });
      }

      await onRefresh();
      setSelectedPokemons([]);
      setIsMultiSelecting(false);
      setShowReleaseConfirm(false);
      Alert(
        `${selectedPokemons.length} Pokémon ${
          selectedPokemons.length > 1 ? "have" : "has"
        } been released.`,
      );
    } catch (err) {
      console.error(err);
      Alert("An error occurred while releasing Pokémon");
    } finally {
      setIsReleasing(false);
    }
  };
  const handleClickMove = () => {
    if (selectedPokemons.length === 0) {
      Alert("You must select at least 1 Pokémon to move");
      return;
    }

    setShowMoveDialog(true);
  };

  const handleSendSuccess = () => {
    setShowSendDialog(false);
    setSelectedPokemons([]);
    setIsMultiSelecting(false);
    void onRefresh();
  };

  const handleClickCancel = () => {
    setSelectedPokemons([]);
    setIsMultiSelecting(false);
  };

  const handleTypeToggle = (type: PokemonType) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  const handleTalentTypeToggle = (type: PokemonType) => {
    setSelectedTalentTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  const toggleFilterDropdown = () => {
    setIsFilterDropdownOpen((prev) => !prev);
  };

  const selectedBox = boxes.find((b) => b.id === selectedBoxId);
  const activeColor =
    selectedBoxId === "all" ? "#3b2c35" : selectedBox?.color || "#6c447d";

  const ItemComponent = viewMode === "mid" ? TCGPokeItem : null;

  const PokemonLoadingSkeleton = ({
    viewMode,
  }: {
    viewMode: keyof typeof VIEW_MODE_GRID_COLS;
  }) => {
    const height = viewMode === "card" ? "h-64" : "h-32";
    return (
      <div
        className={`flex flex-col rounded-sm animate-pulse items-center justify-center relative ${height} bg-pk-gray-2/50`}
      ></div>
    );
  };

  // Filter pokemons based on egg-only filter and search query
  const filteredPokemons =
    optimisticPokemons
      ?.filter((pokemon) => {
        // Box filter
        if (selectedBoxId !== "all") {
          if (selectedBoxId === "default") {
            if (pokemon.box_id !== null && pokemon.box_id !== "default") {
              return false;
            }
          } else if (pokemon.box_id !== selectedBoxId) {
            return false;
          }
        }

        // Eggs are no longer a usable inventory item (breeding/hatching removed)
        if (pokemon.state === "EGG") {
          return false;
        }

        // Type filter
        if (selectedTypes.length > 0) {
          const pokeData = POKE_DATA[pokemon.base_pokemon_name as PokeNames];
          if (!pokeData) return false;

          const hasMatchingType = pokeData.types.some((type) =>
            selectedTypes.some(
              (t) => String(t).toUpperCase() === String(type).toUpperCase(),
            ),
          );
          if (!hasMatchingType) {
            return false;
          }
        }

        // Talent filter
        if (selectedTalentTypes.length > 0) {
          const talentCount =
            pokemon.talents?.filter((t) =>
              selectedTalentTypes.some(
                (selectedType) =>
                  String(t).toUpperCase() ===
                  String(selectedType).toUpperCase(),
              ),
            ).length || 0;
          if (talentCount < talentFilterCount) {
            return false;
          }
        }

        // Search filter
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          const pokemonName = pokemon.base_pokemon_name.toLowerCase();
          const displayId = pokemon.display_id.toLowerCase();

          return pokemonName.includes(query) || displayId.includes(query);
        }

        return true;
      })
      .sort((a, b) => b.level - a.level) || [];

  const activePokemon = activeDragId
    ? optimisticPokemons?.find((p) => `pokemon-${p.id}` === activeDragId)
    : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
    >
      <div
        className="z-30 relative flex flex-col shadow-cartoon-sm border-4 border-gray-900 rounded-lg h-fit min-h-0 overflow-visible transition-all duration-300"
        style={{
          backgroundColor: activeColor,
        }}
      >
        {/* Inner Border mimicking StandardFrame */}
        <div className="z-10 absolute inset-0 bg-black/50 border-4 border-white/20 pointer-events-none" />

        {/* Texture */}
        <div
          className="z-0 absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "url(/assets/app-ui/glass-2.png)",
            backgroundSize: "cover",
            backgroundPosition: "left",
            backgroundRepeat: "repeat",
            mixBlendMode: "multiply",
            opacity: 0.35,
          }}
        />

        <div className="z-55 relative flex items-start gap-2 p-2">
          {/* Filters  */}
          <div className="" ref={filterDropdownRef}>
            <button
              onClick={toggleFilterDropdown}
              className={classNames(
                `p-2 rounded-sm transition-all duration-200 cursor-pointer h-10 w-10 shadow-cartoon-sm  active:scale-95`,
                {
                  "bg-pk-orange-2 hover:bg-pk-orange-3 active:bg-pk-orange-4":
                    selectedTypes.length > 0 || selectedTalentTypes.length > 0,
                  "bg-pk-gray-2 ":
                    selectedTypes.length === 0 &&
                    selectedTalentTypes.length === 0,
                  "hover:bg-pk-gray-1 active:bg-pk-purple-2 hover:shadow-cartoon": true,
                },
              )}
            >
              <FilterIcon
                width={24}
                height={24}
                stroke="#f4eae7"
                strokeWidth={3}
              />
            </button>

            {isFilterDropdownOpen && (
              <div className="top-12 left-0 z-[200] absolute bg-pk-gray-2 shadow-cartoon p-2 border-2 border-pk-gray-4 rounded-md min-w-[260px] max-h-[550px] overflow-hidden">
                {/* Header with minimize toggle */}
                <div className="flex justify-between items-center mb-2">
                  <div className="font-medium text-pk-gray-5 text-xs">
                    Filter by Species Type:
                  </div>
                  <button
                    onClick={() => setIsFilterMinimized(!isFilterMinimized)}
                    className="text-pk-gray-4 hover:text-pk-gray-5 text-xs transition-colors"
                  >
                    {isFilterMinimized ? "▼" : "▲"}
                  </button>
                </div>

                {/* Content area with scroll */}
                <div
                  className={`${
                    isFilterMinimized ? "max-h-0" : "max-h-[280px]"
                  } overflow-y-auto transition-all duration-300`}
                >
                  <div className="gap-1 grid grid-cols-2">
                    {Object.values(PokemonType).map((type) => {
                      const typeName = type.toLowerCase();
                      const typeColor = typeColors[typeName];
                      return (
                        <label
                          key={type}
                          className="flex items-center gap-1 hover:bg-pk-gray-1 px-1 py-0.5 rounded transition-colors cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedTypes.includes(type)}
                            onChange={() => handleTypeToggle(type)}
                            className="bg-pk-gray-3 border-pk-gray-4 rounded focus:ring-pk-orange-4 w-3 h-3 text-pk-orange-4"
                          />
                          <span
                            className="font-medium text-xs capitalize"
                            style={{ color: typeColor }}
                          >
                            {typeName}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Selected types summary when minimized */}
                {isFilterMinimized && selectedTypes.length > 0 && (
                  <div className="mt-1 pt-2 border-pk-gray-3 border-t">
                    <div className="mb-1 text-pk-gray-4 text-xs">
                      Active filters:
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {selectedTypes.map((type) => {
                        const typeName = type.toLowerCase();
                        const typeColor = typeColors[typeName];
                        return (
                          <span
                            key={type}
                            className="bg-pk-gray-1 px-1 py-0.5 rounded text-xs"
                            style={{ color: typeColor }}
                          >
                            {typeName}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Talent Filter */}
                <div className="mt-2 pt-2 border-pk-gray-3 border-t">
                  <div className="mb-1 font-medium text-pk-gray-5 text-xs">
                    Filter by Talent:
                  </div>
                  <div className="gap-2 grid grid-cols-6 mb-2">
                    {Object.values(PokemonType).map((type) => {
                      const typeName = type.toLowerCase();
                      const typeColor = typeColors[typeName];
                      const isSelected = selectedTalentTypes.includes(type);
                      return (
                        <button
                          key={`talent-${type}`}
                          onClick={() => handleTalentTypeToggle(type)}
                          className={classNames(
                            "w-8 h-8 flex items-center justify-center rounded-sm transition-all relative group shadow-sm",
                            isSelected
                              ? "z-10 outline-2 outline-pk-orange-4 opacity-100"
                              : "opacity-30 hover:opacity-60",
                          )}
                          style={{
                            backgroundColor: typeColor,
                          }}
                          title={typeName}
                        >
                          <img
                            src={`/assets/pokemon-types/${typeName}.svg`}
                            alt={typeName}
                            className="drop-shadow-sm w-5 h-5"
                          />
                        </button>
                      );
                    })}
                  </div>

                  {selectedTalentTypes.length > 0 && (
                    <div className="flex flex-col gap-1 mt-2">
                      <div className="font-semibold text-[10px] text-pk-gray-4 uppercase tracking-wider">
                        Min. Count: {talentFilterCount}+
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                          <button
                            key={num}
                            onClick={() => setTalentFilterCount(num)}
                            className={classNames(
                              "w-7 h-7 flex items-center justify-center text-xs font-bold rounded-sm transition-all shadow-sm",
                              talentFilterCount === num
                                ? "bg-pk-orange-4 text-white shadow-cartoon-sm scale-110"
                                : "bg-pk-gray-3 text-pk-gray-5 hover:bg-pk-gray-1 hover:text-pk-white",
                            )}
                          >
                            {num}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Clear All button */}
                {(selectedTypes.length > 0 ||
                  selectedTalentTypes.length > 0) && (
                  <button
                    onClick={() => {
                      setSelectedTypes([]);
                      setSelectedTalentTypes([]);
                      setTalentFilterCount(1);
                    }}
                    className="bg-pk-purple-2 hover:bg-pk-purple-1 mt-2 px-2 py-1 rounded w-full text-pk-white text-xs transition-colors"
                  >
                    Clear All
                  </button>
                )}
              </div>
            )}
          </div>

          {/* View Mode Selector */}
          <button
            onClick={nextViewMode}
            className="flex items-center justify-center bg-pk-gray-2 hover:bg-pk-gray-1 active:bg-pk-purple-2 text-pk-gray-5 hover:text-pk-orange-4 px-2 rounded-sm h-10 w-10 transition-all duration-200 cursor-pointer shadow-cartoon-sm hover:shadow-cartoon active:scale-95"
          >
            {viewMode === "mid" ? (
              <EyeIcon
                width={24}
                height={24}
                stroke="#f4eae7"
                strokeWidth={3}
              />
            ) : viewMode === "card" ? (
              <EyeOffIcon
                width={24}
                height={24}
                stroke="#f4eae7"
                strokeWidth={3}
              />
            ) : (
              <StarIcon
                width={24}
                height={24}
                fill="#f4eae7"
              />
            )}
          </button>

          {/* Search Section */}
          <div className="flex items-center gap-2 bg-pk-gray-2/50 ml-auto px-2 py-1 border-2 border-pk-gray-4 rounded-md h-full">
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent outline-none w-24 text-white text-xs placeholder:text-xs leading-none"
            />
            <SearchIcon width={14} height={14} stroke="#f4eae7" />
          </div>
        </div>
      </div>

      {!intent && (
        <div className="flex items-center my-1 px-1 h-6">
          {isMultiSelecting ? (
            <div className="flex justify-between items-center bg-pk-beige-3 px-3 py-1.5 border border-pk-purple-3/30 rounded-sm w-full animate-in duration-200 fade-in zoom-in-95">
              <div className="flex items-center gap-4">
                <SimpleButton
                  variant="red"
                  onClick={handleClickCancel}
                  className="!h-full"
                >
                  ✕
                </SimpleButton>

                <div className="flex flex-col">
                  <span className="font-black text-pk-gray-4 text-sm leading-none">
                    {selectedPokemons.length} SELECTED
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <SimpleButton variant="blue" onClick={handleClickMove}>
                  <span className="font-bold text-xs uppercase tracking-wide">
                    Move
                  </span>
                </SimpleButton>

                <SimpleButton
                  onClick={() => setShowReleaseConfirm(true)}
                  variant="red"
                  className="!h-full"
                >
                  <TrashIcon
                    width={16}
                    height={16}
                    fill="currentColor"
                    className="stroke-2"
                  />
                </SimpleButton>
              </div>
            </div>
          ) : (
            <div className="text-[10px] text-pk-gray-4/70 xs:text-sm">
              Hold to select • Drag selected to move
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col flex-1 mt-2 min-h-0">
        <BoxTabs
          boxes={boxes}
          selectedBoxId={selectedBoxId}
          onSelectBox={setSelectedBoxId}
          onAddBox={() => setShowCreateBoxDialog(true)}
          className="px-1.5"
        />

        <div
          className="relative flex flex-col flex-1 shadow-cartoon border-4 border-gray-900 rounded-b-xl rounded-tr-xl overflow-hidden transition-all duration-300"
          style={{
            backgroundColor: activeColor,
          }}
        >
          {/* Inner Border mimicking StandardFrame */}
          <div className="z-10 absolute inset-0 bg-black/70 border-4 border-white/20 pointer-events-none" />

          {/* Texture */}
          <div
            className="z-0 absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: "url(/assets/app-ui/glass-2.png)",
              backgroundSize: "cover",
              backgroundPosition: "left",
              backgroundRepeat: "repeat",
              mixBlendMode: "multiply",
              opacity: 0.35,
            }}
          />

          {/* Loading Overlay */}
          {isLoading && (
            <div className="z-50 absolute inset-0 flex flex-col justify-center items-center bg-black/20 backdrop-blur-sm">
              <div className="mb-4 border-4 border-white border-t-transparent rounded-full w-8 h-8 animate-spin"></div>
              <span className="font-semibold text-white text-lg">
                Loading pokemons...
              </span>
            </div>
          )}

          <div className="z-20 relative flex justify-between items-center mx-1.5 mt-2 px-3 py-1 border-gray-900 border-x-4">
            <span className="font-black text-white text-sm uppercase tracking-wider">
              {selectedBoxId === "all" ? "All Pokémon" : selectedBox?.name}
            </span>
            {selectedBoxId !== "all" && selectedBoxId !== "default" && (
              <div className="flex gap-1">
                <button
                  onClick={() => setShowEditBoxModal(true)}
                  className="hover:bg-white/10 p-1 rounded-sm transition-colors cursor-pointer"
                >
                  <PencilIcon width={14} height={14} className="text-white" />
                </button>
                <button
                  onClick={() => setShowDeleteBoxConfirm(true)}
                  className="hover:bg-red-300/50 p-1 rounded-sm transition-colors cursor-pointer"
                >
                  <TrashIcon width={16} height={16} className="invert" />
                </button>
              </div>
            )}
          </div>

          {/* Poke List Content */}
          <div className="z-10 relative flex-1 mt-1 p-1 min-h-0 overflow-x-hidden overflow-y-auto">
            <div
              className={`grid ${`grid-cols-${VIEW_MODE_GRID_COLS[viewMode]}`} gap-2 w-full mx-auto overflow-y-clip overflow-x-hidden`}
            >
              {/* Poke Components */}
              {error ? (
                <div className="flex flex-col justify-center items-center col-span-full py-8 text-center">
                  <div className="mb-4 text-pk-red-400">
                    <SearchIcon width={48} height={48} stroke="currentColor" />
                  </div>
                  <p className="mb-4 text-pk-white/70 text-sm">{error}</p>
                  <SimpleButton
                    variant="purple"
                    onClick={() => void onRefresh()}
                  >
                    Try Again
                  </SimpleButton>
                </div>
              ) : pokemons === undefined ? (
                Array.from({ length: 12 }).map((_, index) => (
                  <PokemonLoadingSkeleton
                    key={`pokemon-loading-skeleton-${index}`}
                    viewMode={viewMode}
                  />
                ))
              ) : filteredPokemons.length === 0 ? (
                <div className="flex flex-col justify-center items-center col-span-full py-8 text-center">
                  <div className="mb-4 text-pk-white/50">
                    <SearchIcon width={48} height={48} stroke="currentColor" />
                  </div>
                  <p className="text-pk-white/70 text-sm">
                    {searchQuery ||
                    selectedTypes.length > 0 ||
                    selectedTalentTypes.length > 0
                      ? `No pokemons found matching your filters`
                      : "No Pokémon in this squad"}
                  </p>
                </div>
              ) : (
                <>
                  {filteredPokemons.map((pokemon) => {
                    const box =
                      selectedBoxId === "all"
                        ? boxes.find((b) => b.id === pokemon.box_id)
                        : undefined;

                    return (
                      <DraggablePokemon key={pokemon.id} id={pokemon.id}>
                        {viewMode === "card" || viewMode === "mini" ? (
                          <CardPokeItem
                            pokemon={pokemon}
                            selected={isSelected(pokemon)}
                            mini={viewMode === "mini"}
                            isInTeam={
                              intent === Intent.SELECTING_POKEMON_FOR_MAIN_DECK
                                ? mainDeckPokemons.some((p) => p.id === pokemon.id)
                                : false
                            }
                            onClick={() => handleClickPokemon(pokemon)}
                            onLongPress={() => handleLongPressPokemon(pokemon)}
                          />
                        ) : (
                          ItemComponent && <ItemComponent
                            pokemon={pokemon}
                            id={pokemon.display_id}
                            level={pokemon.level}
                            talents={pokemon.talents}
                            selected={isSelected(pokemon)}
                            boxIcon={box?.icon ?? undefined}
                            boxColor={box?.color ?? undefined}
                            isInTeam={
                              intent === Intent.SELECTING_POKEMON_FOR_MAIN_DECK
                                ? mainDeckPokemons.some((p) => p.id === pokemon.id)
                                : false
                            }
                            onClick={() => {
                              handleClickPokemon(pokemon);
                            }}
                            onLongPress={() => handleLongPressPokemon(pokemon)}
                          />
                        )}
                      </DraggablePokemon>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Release Confirmation Dialog */}
      <ReleaseConfirmDialog
        isOpen={showReleaseConfirm}
        onClose={() => setShowReleaseConfirm(false)}
        onConfirm={handleBulkRelease}
        pokemon={selectedPokemons as any}
        isLoading={isReleasing}
      />

      <MoveToBoxDialog
        isOpen={showMoveDialog}
        onClose={() => setShowMoveDialog(false)}
        selectedPokemons={selectedPokemons}
        onMove={(targetBoxId) =>
          handleOptimisticMove(
            targetBoxId,
            selectedPokemons.map((p) => p.id),
          )
        }
        boxes={boxes}
      />

      <CreateBoxDialog
        isOpen={showCreateBoxDialog}
        onClose={() => setShowCreateBoxDialog(false)}
        onSuccess={onRefresh}
      />

      {selectedBox && (
        <BoxEditModal
          isOpen={showEditBoxModal}
          onClose={() => setShowEditBoxModal(false)}
          box={selectedBox}
          onSave={handleUpdateBox}
        />
      )}

      {selectedBox && (
        <DeleteBoxConfirmDialog
          isOpen={showDeleteBoxConfirm}
          onClose={() => setShowDeleteBoxConfirm(false)}
          onConfirm={handleDeleteBox}
          box={selectedBox}
          isLoading={isDeleting}
        />
      )}

      <DragOverlay
        dropAnimation={{
          duration: 200,
          easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
        }}
        zIndex={1000}
        modifiers={[
          ({ transform, activeNodeRect, draggingNodeRect }) => {
            // Fix: Correct the visual position when draggingNodeRect doesn't match activeNodeRect
            // This happens on desktop where the DragOverlay portal is measured at wrong position
            if (activeNodeRect && draggingNodeRect) {
              const offsetX = activeNodeRect.left - draggingNodeRect.left;
              const offsetY = activeNodeRect.top - draggingNodeRect.top;
              if (Math.abs(offsetX) > 1 || Math.abs(offsetY) > 1) {
                return {
                  ...transform,
                  x: transform.x + offsetX,
                  y: transform.y + offsetY,
                };
              }
            }
            return transform;
          },
        ]}
      >
        {activePokemon ? (
          <div
            className="cursor-grabbing pointer-events-none"
            style={{
              opacity: 0.9,
              transform: "scale(0.5)",
              transformOrigin: "top left",
            }}
          >
            <ItemComponent
              pokemon={activePokemon}
              id={activePokemon.display_id}
              level={activePokemon.level}
              talents={activePokemon.talents}
              selected={isSelected(activePokemon)}
              isInTeam={false}
              onClick={() => {}}
            />
            {isSelected(activePokemon) && selectedPokemons.length > 1 && (
              <div className="-top-2 -right-2 z-50 absolute flex justify-center items-center bg-pk-orange-4 shadow-md border-2 border-white rounded-full w-6 h-6 font-bold text-white text-xs">
                {selectedPokemons.length}
              </div>
            )}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
